import { Attachment, FileGenerationStatus, Message, MessageRole } from '../types/chat';
import { resolveDisplayGenerationIndex } from 'utils/chat-utilities';
import { allSupportedImageExtensions } from '../types/model.types';

const MARKDOWN_EXTENSIONS = new Set(['md', 'markdown', 'mdx']);
const HTML_EXTENSIONS = new Set(['html', 'htm', 'dashboard']);
const JSON_EXTENSIONS = new Set(['json']);
const CSV_EXTENSIONS = new Set(['csv', 'tsv']);
/** Plain text shown with monospace + line numbers (Claude TXT preview). */
const PLAIN_TEXT_EXTENSIONS = new Set(['txt', 'log', 'text']);
const PDF_EXTENSIONS = new Set(['pdf']);
/** Office docs that can be embedded via Microsoft/Google online viewers. */
const OFFICE_DOCUMENT_EXTENSIONS = new Set([
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
]);
const SPREADSHEET_EXTENSIONS = new Set(['xls', 'xlsx']);
const CODE_EXTENSIONS = new Set([
  'json',
  'js',
  'jsx',
  'ts',
  'tsx',
  'py',
  'css',
  'scss',
  'html',
  'htm',
  'xml',
  'yml',
  'yaml',
  'sh',
  'bash',
  'sql',
  'toml',
]);

const TEXT_PREVIEW_EXTENSIONS = new Set([
  ...MARKDOWN_EXTENSIONS,
  ...HTML_EXTENSIONS,
  ...JSON_EXTENSIONS,
  'txt',
  'csv',
  'tsv',
  'xml',
  'yml',
  'yaml',
  'js',
  'jsx',
  'ts',
  'tsx',
  'py',
  'css',
  'scss',
  'sh',
  'bash',
  'sql',
  'log',
  'toml',
  'ini',
  'env',
  'svg',
]);

const LANGUAGE_BY_EXTENSION: Record<string, string> = {
  md: 'markdown',
  markdown: 'markdown',
  mdx: 'markdown',
  json: 'json',
  js: 'javascript',
  jsx: 'jsx',
  ts: 'typescript',
  tsx: 'tsx',
  py: 'python',
  css: 'css',
  scss: 'scss',
  html: 'html',
  htm: 'html',
  dashboard: 'html',
  xml: 'xml',
  yml: 'yaml',
  yaml: 'yaml',
  sh: 'bash',
  bash: 'bash',
  sql: 'sql',
  csv: 'csv',
  tsv: 'tsv',
  txt: 'text',
  log: 'text',
  toml: 'toml',
  svg: 'xml',
};

export const getAttachmentExtension = (fileName: string, extension?: string) =>
  (extension || fileName.split('.').pop() || '').replace(/^\./, '').toLowerCase();

export const getAttachmentDisplayName = (fileName: string, extension?: string) => {
  const ext = getAttachmentExtension(fileName, extension);
  if (!ext) return fileName;
  return fileName.replace(new RegExp(`\\.${ext}$`, 'i'), '') || fileName;
};

export const isImageAttachment = (fileName: string, extension?: string) => {
  const ext = getAttachmentExtension(fileName, extension);
  return allSupportedImageExtensions.includes(`.${ext}`);
};

export const canOpenAssistantAttachmentInViewer = (file: Attachment): boolean =>
  Boolean(file.fileId) &&
  !isImageAttachment(file.fileName, file.extension) &&
  file.status !== FileGenerationStatus.IN_PROGRESS &&
  file.status !== FileGenerationStatus.FAILED;

export const getLastViewableAssistantAttachment = (
  attachments?: Attachment[]
): Attachment | null => {
  const viewable = (attachments ?? []).filter(
    (attachment) =>
      attachment.sender === 'assistant' && canOpenAssistantAttachmentInViewer(attachment)
  );
  return viewable.length > 0 ? viewable[viewable.length - 1]! : null;
};

export const isMarkdownAttachment = (fileName: string, extension?: string) =>
  MARKDOWN_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

export const isHtmlAttachment = (fileName: string, extension?: string) =>
  HTML_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

/** Generated dashboards download as a ZIP export and show their title instead of the file name. */
export const isDashboardAttachment = (fileName: string, extension?: string) =>
  getAttachmentExtension(fileName, extension) === 'dashboard';

type AttachmentFilter = (fileName: string, extension?: string) => boolean;

const dashboardFileInGeneration = (
  generation: Message['generations'][number] | undefined,
  matches: AttachmentFilter
): Attachment | null => {
  // History stores generated files in generatedImages (including documents);
  // streaming events use attachments. Both belong to the same generation.
  const attachments = [...(generation?.generatedImages ?? []), ...(generation?.attachments ?? [])];
  for (let fileIndex = attachments.length - 1; fileIndex >= 0; fileIndex -= 1) {
    const file = attachments[fileIndex];
    if (
      file &&
      file.sender === MessageRole.ASSISTANT &&
      canOpenAssistantAttachmentInViewer(file) &&
      matches(file.fileName, file.extension)
    ) {
      return file;
    }
  }
  return null;
};

const lastDisplayedFile = (messages: Message[], matches: AttachmentFilter): Attachment | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role !== MessageRole.ASSISTANT) continue;

    for (
      let generationIndex = resolveDisplayGenerationIndex(message);
      generationIndex >= 0;
      generationIndex -= 1
    ) {
      const file = dashboardFileInGeneration(message.generations?.[generationIndex], matches);
      if (file) return file;
    }
  }
  return null;
};

/**
 * Latest successfully saved dashboard on the displayed branch. A regeneration that
 * produced no dashboard keeps the previous revision of the same turn; later,
 * unselected regenerations are never shown. HTML pages saved beside a `.dashboard`
 * file stay out of the canvas; a thread without one (older dashboards were saved
 * as HTML) shows its latest HTML file.
 */
export const getLastDashboardHtmlAttachment = (messages: Message[]): Attachment | null =>
  lastDisplayedFile(messages, isDashboardAttachment) ?? lastDisplayedFile(messages, isHtmlAttachment);

/** Whether any assistant message of the thread, in any generation, holds a `.dashboard` file. */
export const hasDashboardFile = (messages: Message[]): boolean =>
  messages.some(
    (message) =>
      message?.role === MessageRole.ASSISTANT &&
      (message.generations ?? []).some((generation) =>
        [...(generation.generatedImages ?? []), ...(generation.attachments ?? [])].some(
          (file) =>
            file.sender === MessageRole.ASSISTANT && isDashboardAttachment(file.fileName, file.extension)
        )
      )
  );

/**
 * 1, 2, 3… for each agent `.dashboard` file in the loaded messages, oldest first, across all
 * generations; the number shown for versions saved before the backend stored one.
 */
export const getDashboardVersionOrdinals = (messages: Message[]): Record<string, number> => {
  const ordinals: Record<string, number> = {};
  let count = 0;
  for (const message of messages) {
    if (message?.role !== MessageRole.ASSISTANT) continue;
    for (const generation of message.generations ?? []) {
      for (const file of [...(generation.generatedImages ?? []), ...(generation.attachments ?? [])]) {
        if (
          file.sender === MessageRole.ASSISTANT &&
          isDashboardAttachment(file.fileName, file.extension) &&
          !ordinals[file.fileId]
        ) {
          count += 1;
          ordinals[file.fileId] = count;
        }
      }
    }
  }
  return ordinals;
};

/**
 * Dashboard chats show their dashboard in the dashboard panel. Once the thread holds a
 * `.dashboard` file, HTML pages open in the file preview like every other file; before
 * that (older dashboards were saved as HTML) any HTML file opens in the panel.
 */
export const shouldPreviewHtmlInDashboard = (
  fileName: string,
  extension: string | undefined,
  isDashboardConversation: boolean,
  threadHasDashboardFile = false
) =>
  isDashboardConversation &&
  (threadHasDashboardFile
    ? isDashboardAttachment(fileName, extension)
    : isHtmlAttachment(fileName, extension));

export const isJsonAttachment = (fileName: string, extension?: string) =>
  JSON_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

export const isCsvAttachment = (fileName: string, extension?: string) => {
  const ext = getAttachmentExtension(fileName, extension);
  if (CSV_EXTENSIONS.has(ext)) return true;
  // Some payloads omit extension; fall back to filename suffix.
  const lowerName = (fileName || '').toLowerCase();
  return lowerName.endsWith('.csv') || lowerName.endsWith('.tsv');
};

/** Plain .txt/.log — Claude-style monospace preview with line numbers (not markdown). */
export const isPlainTextAttachment = (fileName: string, extension?: string) =>
  PLAIN_TEXT_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

export const isPdfAttachment = (fileName: string, extension?: string) =>
  PDF_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

export const isOfficeDocumentAttachment = (fileName: string, extension?: string) =>
  OFFICE_DOCUMENT_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

/** Binary files previewed via iframe/PDF/Office viewer (not as text/code). */
export const isBinaryDocumentAttachment = (fileName: string, extension?: string) =>
  isPdfAttachment(fileName, extension) || isOfficeDocumentAttachment(fileName, extension);

const COPYABLE_FILE_EXTENSIONS = new Set([
  ...MARKDOWN_EXTENSIONS,
  ...HTML_EXTENSIONS,
  ...JSON_EXTENSIONS,
  ...CSV_EXTENSIONS,
  'txt',
]);

const DOWNLOAD_ONLY_FILE_EXTENSIONS = new Set(['pdf', 'docx', 'pptx', 'xlsx']);

/** File types that support copy in the assistant file viewer header. */
export const supportsAssistantFileCopy = (fileName: string, extension?: string) =>
  COPYABLE_FILE_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

/** File types that support download in the assistant file viewer header. */
export const supportsAssistantFileDownload = (fileName: string, extension?: string) => {
  const ext = getAttachmentExtension(fileName, extension);
  return COPYABLE_FILE_EXTENSIONS.has(ext) || DOWNLOAD_ONLY_FILE_EXTENSIONS.has(ext);
};

/**
 * Whether the viewer should show Preview/Code mode toggle (Claude-style).
 * Hidden for PDF/Office embeds and for JSON/TXT which always stay in Preview.
 */
export const supportsFileViewModeToggle = (fileName: string, extension?: string) => {
  if (isBinaryDocumentAttachment(fileName, extension)) return false;
  if (isPlainTextAttachment(fileName, extension)) return false;
  if (isJsonAttachment(fileName, extension)) return false;
  return true;
};

export const isTextPreviewableAttachment = (fileName: string, extension?: string) =>
  TEXT_PREVIEW_EXTENSIONS.has(getAttachmentExtension(fileName, extension));

export const getAttachmentHighlightLanguage = (fileName: string, extension?: string) =>
  LANGUAGE_BY_EXTENSION[getAttachmentExtension(fileName, extension)] || 'text';

/** Claude-style kind label shown on the attachment card (e.g. Table · CSV). */
export const getAttachmentKindLabel = (fileName: string, extension?: string) => {
  const ext = getAttachmentExtension(fileName, extension);
  if (CSV_EXTENSIONS.has(ext)) return 'Table';
  if (SPREADSHEET_EXTENSIONS.has(ext)) return 'Spreadsheet';
  if (CODE_EXTENSIONS.has(ext)) return 'Code';
  if (
    PDF_EXTENSIONS.has(ext) ||
    OFFICE_DOCUMENT_EXTENSIONS.has(ext) ||
    MARKDOWN_EXTENSIONS.has(ext) ||
    PLAIN_TEXT_EXTENSIONS.has(ext)
  ) {
    return 'Document';
  }
  return 'Document';
};

/** Split plain text into lines for numbered TXT preview (preserves empty trailing lines). */
export const splitTextPreviewLines = (content: string): string[] => {
  const normalized = content.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (normalized === '') return [''];
  return normalized.split('\n');
};

export const getOfficeEmbedUrl = (openUrl: string) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(openUrl)}`;

export const getGoogleDocsViewerUrl = (openUrl: string) =>
  `https://docs.google.com/viewer?url=${encodeURIComponent(openUrl)}&embedded=true`;

/** External viewers need a publicly reachable HTTPS URL (e.g. Azure SAS), not our auth get-file API. */
export const isEmbeddableFileUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    if (parsed.searchParams.has('FileId') && parsed.searchParams.has('ProjectKey')) return false;
    return true;
  } catch {
    return false;
  }
};

/**
 * Parse a single CSV/TSV line with quoted-field support.
 * Handles commas/tabs inside quotes and escaped double-quotes ("").
 */
const parseDelimitedLine = (line: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      cells.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells;
};

export type ParsedDelimitedTable = {
  headers: string[];
  rows: string[][];
};

/** Parse CSV/TSV text into headers + rows for table preview. */
export const parseDelimitedTable = (
  content: string,
  extension?: string
): ParsedDelimitedTable | null => {
  const normalized = content.replace(/^\uFEFF/, '').trimEnd();
  if (!normalized.trim()) return null;

  const ext = (extension || '').toLowerCase();
  const firstLine = normalized.split(/\r?\n/).find((line) => line.length > 0) || '';

  let delimiter = ',';
  if (ext === 'tsv') {
    delimiter = '\t';
  } else {
    const commaCount = (firstLine.match(/,/g) || []).length;
    const tabCount = (firstLine.match(/\t/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    if (tabCount > commaCount && tabCount >= semicolonCount) {
      delimiter = '\t';
    } else if (semicolonCount > commaCount) {
      delimiter = ';';
    }
  }

  const lines = normalized.split(/\r?\n/).filter((line) => line.length > 0);
  if (lines.length === 0) return null;

  const parsedRows = lines.map((line) => parseDelimitedLine(line, delimiter));
  const columnCount = Math.max(...parsedRows.map((row) => row.length));
  if (columnCount === 0) return null;

  const normalizeRow = (row: string[]) => {
    const next = [...row];
    while (next.length < columnCount) next.push('');
    return next.slice(0, columnCount);
  };

  const [headerRow, ...bodyRows] = parsedRows;
  return {
    headers: normalizeRow(headerRow),
    rows: bodyRows.map(normalizeRow),
  };
};